import { Box, Paper, styled, useTheme } from "@mui/material";
import { GetFieldLookup, GetFieldLookupItem, useFormMetadata, useLookups, useSnackbar, type ILookup } from "@ngt/forms";
import { FC, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormDefinitions, LotProperty, LotPropertyBulkEditRow, Property } from "../api/dtos";
import SetLinks from "../components/layout/SetLinks";
import LotPropertyBulkAssignPanel from "../components/LotPropertyBulkAssignPanel";
import { StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppEditingContext, AppScalarValue } from "../components/tables/AppTable.types";
import { LotPropertyProjectCreateLink, LotPropertyStageCreateLink } from "../contexts/layouts/LinkContext";
import PropertySelectForm from "../forms/PropertySelectForm";
import useLotPropertiesByProjectIdStageIdPropertyIds from "../hooks/data/useLotPropertiesByProjectIdStageIdPropertyIds";
import usePostLotPropertyEdit from "../hooks/data/usePostLotPropertyEdit";
import useProperties from "../hooks/data/useProperties";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));
const PROPERTY_COLUMN_PREFIX = "property:";

function updateRow(row: LotPropertyBulkEditRow, properties: LotProperty[]): LotPropertyBulkEditRow {
    return Object.assign(Object.create(Object.getPrototypeOf(row)) as LotPropertyBulkEditRow, row, {
        properties,
    });
}

export function setLotPropertyValue(
    row: LotPropertyBulkEditRow,
    propertyId: number,
    value: AppScalarValue,
): LotPropertyBulkEditRow {
    const properties = [...(row.properties ?? [])];
    const existingIndex = properties.findIndex((property) => property.propertyId === propertyId);
    const existing = existingIndex >= 0 ? properties[existingIndex] : undefined;
    const updated = existing
        ? Object.assign(Object.create(Object.getPrototypeOf(existing)) as LotProperty, existing)
        : new LotProperty({
              lotId: row.lotId,
              propertyId,
          });

    updated.value = typeof value === "string" ? value : undefined;

    if (existingIndex >= 0) {
        properties[existingIndex] = updated;
    } else {
        properties.push(updated);
    }

    return updateRow(row, properties);
}

function lotOptions(lookups: ILookup[] | null | undefined) {
    return (GetFieldLookup(lookups, "LotId")?.items ?? []).flatMap((item) => {
        if (item.id === null || item.id === undefined || item.id === "") {
            return [];
        }

        const value = typeof item.id === "number" ? item.id : Number(item.id);

        return Number.isFinite(value)
            ? [
                  {
                      value,
                      label: item.value?.toString() ?? String(value),
                  },
              ]
            : [];
    });
}

export const buildLotPropertyColumns = (
    properties: readonly Property[],
    lookups: ILookup[] | null | undefined,
): AppColumn<LotPropertyBulkEditRow>[] => {
    const columns: AppColumn<LotPropertyBulkEditRow>[] = [
        {
            id: "lotId",
            label: "Lot Number",
            accessor: (row) => row.lotId,
            scalarType: "lookup",
            flex: 1,
            lookup: {
                options: lotOptions(lookups),
                labelFor: (value) =>
                    value === null || value === undefined
                        ? ""
                        : (GetFieldLookupItem(lookups, "LotId", value)?.value?.toString() ?? String(value)),
            },
            exportAccessor: ({ value }) => value,
        },
    ];

    properties.forEach((property) => {
        if (property.id == null) {
            return;
        }

        const propertyId = property.id;

        columns.push({
            id: `${PROPERTY_COLUMN_PREFIX}${propertyId}`,
            label: property.name ?? String(propertyId),
            accessor: (row) => row.properties?.find((cell) => cell.propertyId === propertyId)?.value,
            scalarType: "text",
            flex: 1,
            setValue: (row, value) => setLotPropertyValue(row, propertyId, value),
        });
    });

    return columns;
};

function errorMessages(reason: unknown): string[] {
    if (typeof reason !== "object" || reason === null) {
        return ["Unable to save lot property."];
    }

    const responseStatus = (reason as { responseStatus?: { errors?: readonly { message?: string }[] } }).responseStatus;
    const messages = responseStatus?.errors
        ?.map((error) => error.message)
        .filter((message): message is string => Boolean(message));

    return messages?.length ? messages : ["Unable to save lot property."];
}

function propertyIdFromColumn(columnId: string | undefined): number | undefined {
    if (!columnId?.startsWith(PROPERTY_COLUMN_PREFIX)) {
        return undefined;
    }

    const suffix = columnId.slice(PROPERTY_COLUMN_PREFIX.length);

    if (suffix.trim() === "") {
        return undefined;
    }

    const value = Number(suffix);

    return Number.isFinite(value) ? value : undefined;
}

export const BulkEditLotProperties: FC = () => {
    const params = useParams();
    const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const projectId = Number(params.projectId);
    const stageId = Number(params.stageId);
    const lotPropertyMetadata = useFormMetadata(FormDefinitions.LotProperty);
    const theme = useTheme();
    const submit = usePostLotPropertyEdit();
    const snackbar = useSnackbar();
    const { data: properties } = useProperties();
    const { data: lookups } = useLookups(lotPropertyMetadata);
    const selectedPropertyIds = useMemo(
        () => selectedProperties.flatMap((property) => (property.id == null ? [] : [property.id])),
        [selectedProperties],
    );
    const {
        data: lotProperties,
        loading: lotPropertiesLoading,
        refresh: lotPropertiesRefresh,
    } = useLotPropertiesByProjectIdStageIdPropertyIds(projectId, stageId, selectedPropertyIds);
    const columns = useMemo(() => buildLotPropertyColumns(selectedProperties, lookups), [selectedProperties, lookups]);

    const commit = useCallback(
        async (draft: LotPropertyBulkEditRow, _original: LotPropertyBulkEditRow, context: AppEditingContext) => {
            try {
                const propertyId = propertyIdFromColumn(context.columnId);

                if (propertyId === undefined) {
                    throw new Error("Unable to determine the edited property.");
                }

                if (draft.lotId == null || !Number.isFinite(draft.lotId)) {
                    throw new Error("Lot is missing its domain ID.");
                }

                const value = draft.properties?.find((property) => property.propertyId === propertyId)?.value;

                await submit(draft.lotId, propertyId, value);
                await lotPropertiesRefresh();

                return draft;
            } catch (reason) {
                for (const message of errorMessages(reason))
                    snackbar.enqueueSnackbar(message, {
                        variant: "error-critical",
                    });
                throw reason;
            }
        },
        [submit, lotPropertiesRefresh, snackbar],
    );

    return (
        <>
            <SetLinks
                createProjectLink={LotPropertyProjectCreateLink}
                createStageLink={LotPropertyStageCreateLink}
                lotLinkEnabled={false}
            />
            <PropertySelectForm
                updateProperties={setSelectedProperties}
                initSelection={selectedProperties}
                onClose={() => setFormOpen(false)}
                open={formOpen}
            />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <StyledButton
                    sx={{
                        marginBottom: theme.spacing(2),
                    }}
                    onClick={() => setFormOpen(true)}
                >
                    Select Properties
                </StyledButton>
            </Box>
            <StyledPaper>
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed
                    title="Lots"
                    loading={lotPropertiesLoading}
                    columns={columns}
                    rows={lotProperties ?? []}
                    getRowId={(row) => {
                        if (row.lotId == null) {
                            throw new Error("Lot property row is missing its domain lot ID");
                        }

                        return row.lotId;
                    }}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    groupingEnabled={false}
                    editing={{
                        mode: "cell",
                        onCommit: commit,
                    }}
                    export={false}
                />
            </StyledPaper>
            <LotPropertyBulkAssignPanel projectId={projectId} stageId={stageId} properties={properties ?? []} />
        </>
    );
};

export default BulkEditLotProperties;
